import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'

import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, from, of } from 'rxjs'
import { map, shareReplay, switchMap, takeUntil } from 'rxjs/operators'

import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import { DebugService } from 'app/_angular/service/debug.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { KirjanpitajanTiimi, KirjanpitajanTiimikokonaisuus, KirjanpitajanTiimiTiimikokonaisuudessa } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { KirjanpitajanTiimiService } from 'app/_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { YllapitoUriService } from 'app/_jaettu-lemonator/service/yllapito-uri.service'

import { LisaaTiimiKokonaisuuteenDialog, LisaaTiimiKokonaisuuteenDialogData } from './lisaa-tiimi-kokonaisuuteen.dialog'
import { LisaaTiimikokonaisuusDialog, LisaaTiimikokonaisuusDialogData } from './lisaa-tiimikokonaisuus.dialog'
import { MuokkaaKokonaisuudenTiimijaksoDialog, MuokkaaKokonaisuudenTiimijaksoDialogData } from './muokkaa-kokonaisuuden-tiimijakso.dialog'
import { MuokkaaTiimikokonaisuusDialog, MuokkaaTiimikokonaisuusDialogData } from './muokkaa-tiimikokonaisuus.dialog'

interface NaytettavaTiimiRivi {
  tiimiAvain: KirjanpitajanTiimi['avain']
  tiimiNimi: string
  alku: NumberDate
  loppu: NumberDate | null
  aktiivinen: boolean
}

interface NaytettavaKokonaisuus {
  avain: KirjanpitajanTiimikokonaisuus['avain']
  nimi: string
  aktiivisetTiimit: number
}

interface ValittunKokonaisuusNakyma {
  kokonaisuus: KirjanpitajanTiimikokonaisuus
  tiimirivit: NaytettavaTiimiRivi[]
  kaikkiTiimit: KirjanpitajanTiimi[]
}

@Component({
  templateUrl: './tiimikokonaisuudet.component.html',
  styleUrls: ['./tiimikokonaisuudet.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TiimikokonaisuudetComponent implements OnInit, OnDestroy {
  readonly lataa = signal(true)
  readonly kokonaisuudet = signal<NaytettavaKokonaisuus[]>([])
  varatutAvaimet: string[] = []

  valittunKokonaisuusNakymaObservable: Observable<ValittunKokonaisuusNakyma | null>

  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  tiimitUriObservable: Observable<string>
  tiimitEncodedUriObservable: Observable<string>

  private _valittuAvainSubject = new BehaviorSubject<KirjanpitajanTiimikokonaisuus['avain'] | null>(null)
  private _paivitaSubject = new BehaviorSubject<void>(undefined)
  private _ngUnsubscribe = new Subject<void>()

  constructor(
    private _tiimiService: KirjanpitajanTiimiService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _yllapitoUriService: YllapitoUriService,
    private _debugService: DebugService,
    private _dateService: DateService,
    private _dialog: MatDialog,
    private _errorHandler: ErrorHandler,
  ) {}

  ngOnInit() {
    const tanaan = this._dateService.currentNumberDate()

    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.tiimitUriObservable = of(this._yllapitoUriService.annaKirjanpitajienTiimitUri())
    this.tiimitEncodedUriObservable = this.tiimitUriObservable.pipe(map(uri => this._debugService.createFirestoreLink(uri)))

    const tiimitObservable = this._paivitaSubject.pipe(switchMap(() => from(this._tiimiService.annaKaikkiTiimit())))
    const kokonaisuudetObservable = this._paivitaSubject.pipe(switchMap(() => from(this._tiimiService.annaKaikkiTiimikokonaisuudet())))

    const yhdistettyObservable = combineLatest([tiimitObservable, kokonaisuudetObservable]).pipe(
      map(([tiimit, kokonaisuudet]) => {
        const tiimitMap = new Map<KirjanpitajanTiimi['avain'], KirjanpitajanTiimi>()
        for (const t of tiimit) {
          tiimitMap.set(t.avain, t)
        }
        return { tiimit, tiimitMap, kokonaisuudet }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this._ngUnsubscribe),
    )

    yhdistettyObservable.subscribe({
      next: ({ kokonaisuudet }) => {
        this.kokonaisuudet.set(
          kokonaisuudet
            .map(k => ({
              avain: k.avain,
              nimi: k.nimi,
              aktiivisetTiimit: this._laskeAktiivisetTiimit(k, tanaan),
            }))
            .sort((a, b) => a.nimi.localeCompare(b.nimi)),
        )
        this.varatutAvaimet = kokonaisuudet.map(k => k.avain)
        if (!this._valittuAvainSubject.value && this.kokonaisuudet().length > 0) {
          this._valittuAvainSubject.next(this.kokonaisuudet()[0].avain)
        }
        this.lataa.set(false)
      },
      error: err => {
        this._errorHandler.handleError(err)
        this.lataa.set(false)
      },
    })

    this.valittunKokonaisuusNakymaObservable = combineLatest([this._valittuAvainSubject, yhdistettyObservable]).pipe(
      map(([avain, { tiimit, tiimitMap, kokonaisuudet }]) => {
        if (!avain) {
          return null
        }
        const kokonaisuus = kokonaisuudet.find(k => k.avain === avain)
        if (!kokonaisuus) {
          return null
        }
        const tiimirivit = this._naytettavatTiimit(kokonaisuus.tiimit, tiimitMap)
        return { kokonaisuus, tiimirivit, kaikkiTiimit: tiimit }
      }),
    )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  valitseKokonaisuus(rivi: NaytettavaKokonaisuus) {
    this._valittuAvainSubject.next(rivi.avain)
  }

  get valittuAvain(): KirjanpitajanTiimikokonaisuus['avain'] | null {
    return this._valittuAvainSubject.value
  }

  async muokkaaKokonaisuutta(kokonaisuus: KirjanpitajanTiimikokonaisuus) {
    const data: MuokkaaTiimikokonaisuusDialogData = {
      kokonaisuusAvain: kokonaisuus.avain,
      nykyinenNimi: kokonaisuus.nimi,
      nykyinenAlku: kokonaisuus.alku,
      nykyinenLoppu: kokonaisuus.loppu,
    }
    const ref = this._dialog.open(MuokkaaTiimikokonaisuusDialog, { data, width: '520px', autoFocus: false })
    const muutos = await firstValueFrom(ref.afterClosed())
    if (muutos) {
      this._paivitaSubject.next()
    }
  }

  async lisaaUusiKokonaisuus() {
    const data: LisaaTiimikokonaisuusDialogData = {
      varatutAvaimet: this.varatutAvaimet,
    }
    const ref = this._dialog.open(LisaaTiimikokonaisuusDialog, { data, width: '520px', autoFocus: false })
    const vastaus = await firstValueFrom(ref.afterClosed())
    if (vastaus?.avain) {
      this._valittuAvainSubject.next(vastaus.avain)
      this._paivitaSubject.next()
    }
  }

  async lisaaTiimiKokonaisuuteen(nakyma: ValittunKokonaisuusNakyma) {
    const tanaan = this._dateService.currentNumberDate()
    const aktiivisestiKuuluvatAvaimet = new Set<string>(nakyma.kokonaisuus.tiimit.filter(t => this._onkoAktiivinenPaivamaarana(t.alku, t.loppu, tanaan)).map(t => t.avain))
    const valittavissaOlevatTiimit = nakyma.kaikkiTiimit
      .filter(t => !aktiivisestiKuuluvatAvaimet.has(t.avain))
      .map(t => ({ avain: t.avain, nimi: t.nimi }))
      .sort((a, b) => a.nimi.localeCompare(b.nimi))
    const data: LisaaTiimiKokonaisuuteenDialogData = {
      kokonaisuusAvain: nakyma.kokonaisuus.avain,
      kokonaisuusNimi: nakyma.kokonaisuus.nimi,
      valittavissaOlevatTiimit,
    }
    const ref = this._dialog.open(LisaaTiimiKokonaisuuteenDialog, { data, width: '520px', autoFocus: false })
    const muutos = await firstValueFrom(ref.afterClosed())
    if (muutos) {
      this._paivitaSubject.next()
    }
  }

  async muokkaaTiiminJaksoa(nakyma: ValittunKokonaisuusNakyma, rivi: NaytettavaTiimiRivi) {
    const data: MuokkaaKokonaisuudenTiimijaksoDialogData = {
      kokonaisuusAvain: nakyma.kokonaisuus.avain,
      tiimiAvain: rivi.tiimiAvain,
      tiimiNimi: rivi.tiimiNimi,
      nykyinenAlku: rivi.alku,
      nykyinenLoppu: rivi.loppu,
    }
    const ref = this._dialog.open(MuokkaaKokonaisuudenTiimijaksoDialog, { data, width: '480px', autoFocus: false })
    const muutos = await firstValueFrom(ref.afterClosed())
    if (muutos) {
      this._paivitaSubject.next()
    }
  }

  private _laskeAktiivisetTiimit(kokonaisuus: KirjanpitajanTiimikokonaisuus, paiva: NumberDate): number {
    let maara = 0
    for (const t of kokonaisuus.tiimit ?? []) {
      if (this._onkoAktiivinenPaivamaarana(t.alku, t.loppu, paiva)) {
        maara++
      }
    }
    return maara
  }

  private _naytettavatTiimit(jaksot: KirjanpitajanTiimiTiimikokonaisuudessa[], tiimitMap: Map<KirjanpitajanTiimi['avain'], KirjanpitajanTiimi>): NaytettavaTiimiRivi[] {
    const tanaan = this._dateService.currentNumberDate()
    return (jaksot ?? [])
      .map(j => {
        const tiimi = tiimitMap.get(j.avain)
        return {
          tiimiAvain: j.avain,
          tiimiNimi: tiimi?.nimi ?? j.avain,
          alku: j.alku,
          loppu: j.loppu,
          aktiivinen: this._onkoAktiivinenPaivamaarana(j.alku, j.loppu, tanaan),
        }
      })
      .sort((a, b) => {
        if (a.aktiivinen !== b.aktiivinen) {
          return a.aktiivinen ? -1 : 1
        }
        return a.tiimiNimi.localeCompare(b.tiimiNimi)
      })
  }

  private _onkoAktiivinenPaivamaarana(alku: NumberDate, loppu: NumberDate | null, paiva: NumberDate): boolean {
    if (alku > paiva) {
      return false
    }
    if (loppu !== null && loppu < paiva) {
      return false
    }
    return true
  }
}
